import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, ScrollArea } from "@lyder-talk/ui";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/hooks/use-auth";
import { useProfile, useUserStats } from "@/hooks/use-profile";
import { useProfileSettings } from "@/hooks/use-profile-settings";
import { useGamification } from "@/hooks/use-gamification";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { hairlineClass } from "@/lib/card-styles";

import { ProfileHeader } from "./ProfileHeader";
import { ProfileSectionNav, type ProfileSection } from "./ProfileSectionNav";
import { ProfileInfoSection } from "./sections/ProfileInfoSection";
import { AccountSettingsSection } from "./sections/AccountSettingsSection";
import { StatisticsSection } from "./sections/StatisticsSection";

interface ProfileModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Profile and account settings.
 *
 * This is layout only. Every side effect — avatar upload, credential changes,
 * the OneSignal permission poll — lives in `useProfileSettings`, which is what
 * took the component from 784 lines to this one; the earlier version kept
 * ~20 `useState`s and six handlers inline next to the markup they served.
 *
 * ## Layout
 *
 *   header        `ProfileHeader` — identity, above both layouts
 *   navigation    `ProfileSectionNav` as a rail (desktop) or tabs (mobile)
 *   content       the selected section, in a scroll area
 *
 * The desktop rail is navigation and nothing else. It used to carry the avatar
 * and three stat chips above the section list, which meant the dialog opened
 * with two competing identity statements and pushed the actual form down by
 * ~200px.
 */
export const ProfileModal = ({ open, onOpenChange }: ProfileModalProps) => {
  const { t } = useTranslation();
  const { userId } = useAuth();
  const { isLoading: profileLoading } = useProfile();
  // Stats are four aggregate queries — only pay for them while the dialog is open.
  const { data: stats, isLoading: statsLoading } = useUserStats({ enabled: open });
  const { level, currentStreak, longestStreak } = useGamification();
  const isMobile = useIsMobile();

  const settings = useProfileSettings(open, userId);
  const [activeSection, setActiveSection] = useState<ProfileSection>("profile");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="full"
        variant={isMobile ? "fullscreen" : "default"}
        closeLabel={t("pages.dialogs.close", "Close")}
        className={cn(
          "flex gap-0 overflow-hidden p-0",
          isMobile
            ? "h-[100dvh] max-h-[100dvh] w-full max-w-full flex-col"
            : "h-[85vh] max-h-[85vh] w-[min(95vw,56rem)] max-w-none flex-col rounded-2xl",
        )}
      >
        <DialogTitle className="sr-only">
          {t("settings.profile.modalTitle", "Profile settings")}
        </DialogTitle>

        <ProfileHeader
          userId={userId}
          displayName={settings.displayName}
          email={settings.email}
          avatarUrl={settings.previewUrl ?? settings.avatarUrl}
          currentStreak={currentStreak}
          level={level}
        />

        <div
          className={cn(
            "flex min-h-0 flex-1 flex-col",
            !isMobile && "flex-row border-t",
            !isMobile && hairlineClass,
          )}
        >
          <ProfileSectionNav
            active={activeSection}
            onSelect={setActiveSection}
            variant={isMobile ? "tabs" : "rail"}
            className={cn(!isMobile && "w-56 shrink-0 border-r", hairlineClass)}
            badges={
              settings.notificationPermission === "denied"
                ? {
                    account: (
                      <span className="ml-auto rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-red-700 dark:bg-red-950/50 dark:text-red-300">
                        {t("settings.account.blocked", "Blocked")}
                      </span>
                    ),
                  }
                : undefined
            }
          />

          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            {profileLoading ? (
              <div className="flex flex-1 items-center justify-center">
                <Loader2 className="h-7 w-7 animate-spin text-primary" aria-hidden="true" />
                <span className="sr-only">{t("common.actions.loading", "Loading")}</span>
              </div>
            ) : (
              <ScrollArea className="min-h-0 overflow-x-hidden flex-1">
                <div className="mx-auto min-w-0 w-full max-w-[calc(100vw-2rem)] p-5 sm:max-w-2xl sm:p-6">
                  {activeSection === "profile" && (
                    <ProfileInfoSection
                      userId={userId}
                      email={settings.email}
                      displayName={settings.displayName}
                      setDisplayName={settings.setDisplayName}
                      bio={settings.bio}
                      setBio={settings.setBio}
                      previewUrl={settings.previewUrl}
                      avatarUrl={settings.avatarUrl}
                      uploading={settings.uploading}
                      isUpdating={settings.isUpdating}
                      onImageUpload={settings.uploadAvatar}
                      onRemoveImage={settings.removeAvatar}
                      onSaveProfile={settings.saveProfile}
                    />
                  )}

                  {activeSection === "account" && (
                    <AccountSettingsSection
                      email={settings.email}
                      changeEmail={settings.changeEmail}
                      changePassword={settings.changePassword}
                      isChangingEmail={settings.isChangingEmail}
                      isChangingPassword={settings.isChangingPassword}
                      notificationPermission={settings.notificationPermission}
                      isRequestingPermission={settings.isRequestingPermission}
                      onRequestNotificationPermission={settings.requestPermission}
                    />
                  )}

                  {activeSection === "statistics" && (
                    <StatisticsSection
                      stats={stats}
                      isLoading={statsLoading}
                      currentStreak={currentStreak}
                      longestStreak={longestStreak}
                    />
                  )}
                </div>
              </ScrollArea>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileModal;